import { Injectable } from '@angular/core';
import {
  MCAuthoritiesEventsEconomy,
  MCCodeListWrapperService,
  MCCustomFunctionsParams,
  MCFormisFieldAutoCompleteService,
} from '@maslul/common-services';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SelectItem } from 'primeng/api';

@Injectable({
  providedIn: 'root',
})
export class AuthoritiesEventsEconomy extends MCAuthoritiesEventsEconomy {
  // ========== Standards Exempt PI - Product & Customs Item ==========

  getProductTypeSelectionList(
    customFunctionsObj: MCCustomFunctionsParams,
  ): Observable<SelectItem[]> {
    const codeListService = customFunctionsObj.injector.get(
      MCCodeListWrapperService,
    );

    return codeListService
      .getListAllColums('rqlListEconomyPersonalAchuda', true, false)
      .pipe(
        map((items) => {
          // רשימת מוצרים ייחודית לפי customsDescription
          const uniqueMap = new Map<string, SelectItem>();
          items.forEach((item) => {
            const description =
              item.additionalColumns?.['customsDescription'] || '';
            const productId = item.value; // מזהה מספרי של המוצר
            if (!uniqueMap.has(description)) {
              uniqueMap.set(description, {
                value: productId,
                label: description,
              });
            }
          });
          return Array.from(uniqueMap.values());
        }),
      );
  }

  onProductTypeSelected(customFunctionsObj: MCCustomFunctionsParams) {
    const selectedValue = customFunctionsObj.value;
    // התעלמות מאירוע פתיחת autocomplete
    const value =
      selectedValue?.value?.value || selectedValue?.value || selectedValue;
    if (value.originalEvent || !value) return;

    // איפוס שדה בחירת פרט מכס ושדה פרט מכס בעת החלפת מוצר
    const customsItemSelectionField =
      this.getCustomsItemSelectionField(customFunctionsObj);
    if (customsItemSelectionField?.formControl) {
      customsItemSelectionField.formControl.setValue(null);
    }
    const customItemField = this.getCustomItemField(customFunctionsObj);
    if (customItemField?.formControl) {
      customItemField.formControl.setValue(null);
    }

    this.updateCustomsItemSelectionList(
      customFunctionsObj,
      selectedValue,
      false,
    );
  }

  initCustomsItemSelectionList(customFunctionsObj: MCCustomFunctionsParams) {
    // טעינת רשימת פרטי מכס לפי המוצר שכבר נבחר (בעת חזרה לטאב)
    const productField = this.getProductTypeSelectionField(customFunctionsObj);
    const productValue = productField?.formControl?.value;
    if (!productValue) return;

    this.updateCustomsItemSelectionList(
      customFunctionsObj,
      productValue,
      false,
    );
  }

  onCustomsItemSelectionChanged(customFunctionsObj: MCCustomFunctionsParams) {
    const selectedValue = customFunctionsObj.value;
    // התעלמות מאירוע פתיחת autocomplete
    const value =
      selectedValue?.value?.value || selectedValue?.value || selectedValue;
    if (value.originalEvent || !value) return;

    if (value === '999') {
      // נבחר "אחר" - פתיחת שדה פרט מכס לעריכה ידנית
      this.showCustomItemForManualEdit(customFunctionsObj);
    } else {
      // נבחר פרט מכס מהרשימה - עדכון שדה customItem (text = 4 ספרות פרט מכס)
      this.setCustomItemFromSelection(customFunctionsObj, value);
    }
  }

  // ========== Private Methods ==========

  private updateCustomsItemSelectionList(
    customFunctionsObj: MCCustomFunctionsParams,
    selectedProduct: any,
    resetSelection: boolean,
  ) {
    const codeListService = customFunctionsObj.injector.get(
      MCCodeListWrapperService,
    );
    const formisFieldAutoCompleteService = customFunctionsObj.injector.get(
      MCFormisFieldAutoCompleteService,
    );

    // מזהה המוצר שנבחר (value מהקוד ליסט)
    const productId = String(
      selectedProduct?.value?.value ||
        selectedProduct?.value ||
        selectedProduct,
    );

    codeListService
      .getListAllColums('rqlListEconomyPersonalAchuda', true, false)
      .pipe(
        map((items) => {
          // סינון פרטי מכס לפי value (מזהה המוצר), מציג label (4 ספרות פרט מכס)
          const filtered = items
            .filter((item) => String(item.value) === productId)
            .map((item) => ({
              value: item.label,
              label: item.label,
            }));
          filtered.push({ value: '999', label: 'אחר' });
          return filtered;
        }),
      )
      .subscribe((options) => {
        const customsItemSelectionField =
          this.getCustomsItemSelectionField(customFunctionsObj);
        if (customsItemSelectionField?.props) {
          customsItemSelectionField.props.options = options;
          if (resetSelection) {
            customsItemSelectionField.formControl?.reset();
          }
          formisFieldAutoCompleteService.externalRefreshList.next(true);
        }
      });
  }

  private showCustomItemForManualEdit(
    customFunctionsObj: MCCustomFunctionsParams,
  ) {
    const customItemField = this.getCustomItemField(customFunctionsObj);
    if (customItemField) {
      if (customItemField.props) {
        customItemField.props.options = [];
      }
      customItemField.formControl?.setValue(null);
      customItemField.hide = false;
    }
  }

  private setCustomItemFromSelection(
    customFunctionsObj: MCCustomFunctionsParams,
    itemValue: string,
  ) {
    const formisFieldAutoCompleteService = customFunctionsObj.injector.get(
      MCFormisFieldAutoCompleteService,
    );
    const customItemField = this.getCustomItemField(customFunctionsObj);

    if (!itemValue || !this.isValidItem(itemValue)) return;

    // text = 4 ספרות פרט מכס, מרפדים ל-10
    const paddedItemNum = this.padTo10Digits(itemValue);

    if (customItemField?.props) {
      customItemField.props.options = [
        { value: paddedItemNum, label: paddedItemNum },
      ];
      customItemField.formControl?.setValue({
        value: paddedItemNum,
        label: paddedItemNum,
      });
      customItemField.hide = true;
      formisFieldAutoCompleteService.externalRefreshList.next(true);
    }
  }

  private isValidItem(item4digits: string): boolean {
    return (
      !!item4digits && item4digits.length >= 4 && /^\d+$/.test(item4digits)
    );
  }

  private padTo10Digits(item4digits: string): string {
    return item4digits + '000000';
  }

  private getCustomItemField(
    customFunctionsObj: MCCustomFunctionsParams,
  ): FormlyFieldConfig | undefined {
    const customItemSec = customFunctionsObj.formisForm.fields?.find(
      (x) => x.key == 'customItem',
    );

    return customItemSec?.fieldGroup?.find((x) => x.key == 'customItem');
  }

  private getCustomsItemSelectionField(
    customFunctionsObj: MCCustomFunctionsParams,
  ): FormlyFieldConfig | undefined {
    const customItemSec = customFunctionsObj.formisForm.fields?.find(
      (x) => x.key == 'customItem',
    );

    return customItemSec?.fieldGroup?.find(
      (x) => x.key == 'customsItemSelection',
    );
  }

  private getProductTypeSelectionField(
    customFunctionsObj: MCCustomFunctionsParams,
  ): FormlyFieldConfig | undefined {
    const customItemSec = customFunctionsObj.formisForm.fields?.find(
      (x) => x.key == 'customItem',
    );

    return customItemSec?.fieldGroup?.find(
      (x) => x.key == 'productTypeSelection',
    );
  }
}
